import React, { useState, useEffect } from 'react';
import { X, User, RefreshCw, MessageCircle } from 'lucide-react';
import {
  AdminClientApi,
  ClientBelief,
  GetBeliefsInputInterface,
  SimulateFeedContent,
  GetSupportingBeliefCountFromBeliefsInputInterface,
} from '../Admin-ClientLibrary';
import { getApiUrl } from '../config/api';
import './SwitchContentModal.css';

// Initialize the Admin Client Library
const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

interface SwitchContentModalProps {
  isOpen: boolean;
  onClose: () => void;
  contentToSwitch: SimulateFeedContent | null;
}

const SwitchContentModal: React.FC<SwitchContentModalProps> = ({
  isOpen,
  onClose,
  contentToSwitch,
}) => {
  const [beliefs, setBeliefs] = useState<ClientBelief[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [switching, setSwitching] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [powerUserBeliefCounts, setPowerUserBeliefCounts] = useState<Record<number, number>>({});

  // Get thought ID from content
  const getThoughtId = (content: SimulateFeedContent): number | null => {
    if (content.content.type === 'belief' && content.content.content?.thought_id) {
      return content.content.content.thought_id;
    }
    if (content.content.type === 'thought' && content.content.id) {
      return content.content.id;
    }
    return null;
  };

  // Fetch Top user beliefs matching the thought ID
  const fetchPowerUserBeliefs = async (): Promise<void> => {
    if (!contentToSwitch) return;

    const thoughtId = getThoughtId(contentToSwitch);
    if (!thoughtId) {
      setError('Unable to determine thought ID for this content');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const apiParams: GetBeliefsInputInterface = {
        goeUserId: 11, // Top users start from ID 11
        loeUserId: 110, // Top users end at ID 110
        thoughtId: thoughtId,
        limit: 100,
        getCount: true,
      };

      console.log('Fetching Top user beliefs with params:', apiParams);

      const response = await adminApi.getBeliefs(apiParams);

      if (response.success) {
        setBeliefs(response.beliefs);
        console.log(
          `Found ${response.beliefs.length} Top user beliefs for thought ID ${thoughtId}`
        );
        // Fetch supporting belief counts for the retrieved beliefs
        await fetchPowerUserBeliefCounts(response.beliefs);
      } else {
        setError(response.message || 'Failed to fetch Top user beliefs');
        setBeliefs([]);
      }
    } catch (err: any) {
      console.error('Error fetching Top user beliefs:', err);
      setError('Network error occurred while fetching beliefs');
      setBeliefs([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch supporting belief counts for power user beliefs
  const fetchPowerUserBeliefCounts = async (beliefs: ClientBelief[]): Promise<void> => {
    if (beliefs.length === 0) return;

    try {
      const beliefIds = beliefs.map((belief) => belief.id);
      console.log('Fetching supporting belief counts for belief IDs:', beliefIds);

      const apiParams: GetSupportingBeliefCountFromBeliefsInputInterface = {
        beliefIds,
      };

      const response = await adminApi.getSupportingBeliefCountFromBeliefs(apiParams);

      if (response.success) {
        const counts: Record<number, number> = {};
        response.supportingBeliefCounts.forEach((count) => {
          counts[count.beliefId] = count.supportingBeliefCount;
        });

        setPowerUserBeliefCounts((prev) => ({
          ...prev,
          ...counts,
        }));

        console.log('Supporting belief counts:', counts);
      } else {
        console.error('Failed to fetch supporting belief counts:', response.message);
      }
    } catch (err: any) {
      console.error('Error fetching supporting belief counts:', err);
    }
  };

  // Load beliefs when modal opens or content changes
  useEffect(() => {
    if (isOpen && contentToSwitch) {
      fetchPowerUserBeliefs();
    }
  }, [isOpen, contentToSwitch]);

  // Handle belief selection
  const handleBeliefSelect = async (selectedBelief: ClientBelief): Promise<void> => {
    if (!contentToSwitch) return;

    console.log('=== CONTENT SWITCH SELECTION ===');
    console.log('Content to switch:', contentToSwitch);
    console.log('Selected Power user belief:', selectedBelief);
    console.log('Belief position:', selectedBelief.position);
    console.log('Belief user ID:', selectedBelief.user_id);
    console.log('Belief thought text:', selectedBelief.thought?.text);
    console.log('Feed content ID:', contentToSwitch.feed_content_id);
    console.log('================================');

    await performContentSwitch('belief', selectedBelief.id);
  };

  // Handle thought selection
  const handleThoughtSelect = async (): Promise<void> => {
    if (!contentToSwitch) return;

    const thoughtId = getThoughtId(contentToSwitch);
    if (!thoughtId) {
      setError('Unable to determine thought ID for this content');
      return;
    }

    console.log('=== SWITCH TO THOUGHT ===');
    console.log('Content to switch:', contentToSwitch);
    console.log('Switching to thought ID:', thoughtId);
    console.log('Feed content ID:', contentToSwitch.feed_content_id);
    console.log('========================');

    await performContentSwitch('thought', thoughtId);
  };

  // Perform the actual content switch
  const performContentSwitch = async (
    contentType: 'belief' | 'thought',
    contentId: number
  ): Promise<void> => {
    setSwitching(true);
    setError(null);

    try {
      const response = await adminApi.updateContentOnFeed({
        feedContentId: contentToSwitch!.feed_content_id,
        contentType,
        contentId,
      });

      if (response.success) {
        console.log('Successfully switched content:', response);
        alert('Content switched successfully! The page will refresh to show the updated feed.');
        // Refresh the page to show the updated feed content
        window.location.reload();
      } else {
        setError(response.message || 'Failed to switch content');
      }
    } catch (err: any) {
      console.error('Error switching content:', err);
      setError('Network error occurred while switching content');
    } finally {
      setSwitching(false);
    }
  };

  // Get position styling
  const getPositionStyle = (position: string) => {
    switch (position?.toLowerCase()) {
      case 'believe':
        return 'believe';
      case 'disbelieve':
        return 'disbelieve';
      case 'unsure':
        return 'unsure';
      default:
        return 'unsure';
    }
  };

  // Get user initials
  const getUserInitials = (userId: number): string => {
    return `U${userId}`;
  };

  // Check if current content is a belief
  const isCurrentContentBelief = (): boolean => {
    return contentToSwitch?.content.type === 'belief';
  };

  if (!isOpen) return null;

  return (
    <div
      className="switch-modal-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !switching) {
          onClose();
        }
      }}
    >
      <div className="switch-modal">
        <div className="switch-modal-header">
          <h2>Switch Content</h2>
          <button
            className="close-button"
            onClick={switching ? undefined : onClose}
            disabled={switching}
          >
            <X size={24} />
          </button>
        </div>

        <div className="switch-modal-body">
          {contentToSwitch && (
            <div className="current-content-info">
              <h3>Current Content:</h3>
              <div className="content-summary">
                <span className="content-type">{contentToSwitch.content.type}</span>
                <span className="content-rank">Rank #{contentToSwitch.feed_rank}</span>
                {contentToSwitch.content.content?.text && (
                  <span className="content-text">
                    {contentToSwitch.content.content.text.length > 60
                      ? `${contentToSwitch.content.content.text.substring(0, 60)}...`
                      : contentToSwitch.content.content.text}
                  </span>
                )}
              </div>
            </div>
          )}

          {isCurrentContentBelief() && (
            <div className="thought-switch-section">
              <h3>
                <MessageCircle size={18} />
                Switch to Underlying Thought
              </h3>
              <div className="thought-switch-option">
                <button
                  className={`thought-switch-btn ${switching ? 'disabled' : ''}`}
                  onClick={() => !switching && handleThoughtSelect()}
                  disabled={switching}
                >
                  <MessageCircle size={16} />
                  <div className="thought-switch-content">
                    <span className="thought-switch-title">Show Original Thought</span>
                    <span className="thought-switch-description">
                      Switch to the thought this belief is based on
                    </span>
                  </div>
                </button>
              </div>
            </div>
          )}

          <div className="power-users-section">
            <h3>
              <RefreshCw size={18} />
              Top User Beliefs (ID 11-110)
            </h3>

            {loading && (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading Top user beliefs...</p>
              </div>
            )}

            {error && (
              <div className="error-state">
                <p>{error}</p>
                <button className="retry-button" onClick={fetchPowerUserBeliefs}>
                  Retry
                </button>
              </div>
            )}

            {!loading && !error && beliefs.length === 0 && (
              <div className="empty-state">
                <p>No Top user beliefs found for this thought.</p>
              </div>
            )}

            {!loading && !error && beliefs.length > 0 && (
              <div className="beliefs-list">
                {switching && (
                  <div className="switching-overlay">
                    <div className="spinner"></div>
                    <p>Switching content...</p>
                  </div>
                )}
                {beliefs
                  .filter((belief) => {
                    const supportingCount = powerUserBeliefCounts[belief.id] || 0;
                    return supportingCount > 0; // Only show users with supporting beliefs
                  })
                  .map((belief) => (
                    <div
                      key={belief.id}
                      className={`belief-item ${switching ? 'disabled' : ''}`}
                      onClick={() => !switching && handleBeliefSelect(belief)}
                    >
                      <div className="belief-header">
                        <div className="user-avatar">{getUserInitials(belief.user_id)}</div>
                        <div className="belief-info">
                          <div className="belief-user">
                            <span className="user-id">User ID: {belief.user_id}</span>
                            <span className={`position-badge ${getPositionStyle(belief.position)}`}>
                              {belief.position}
                            </span>
                          </div>
                          <div className="belief-meta">
                            <span className="belief-id">Belief ID: {belief.id}</span>
                            <span className="supporting-count">
                              ({powerUserBeliefCounts[belief.id] || 0})
                            </span>
                          </div>
                        </div>
                      </div>
                      {belief.thought?.text && (
                        <div className="belief-text">{belief.thought.text}</div>
                      )}
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SwitchContentModal;
